import { Box, SxProps, Typography } from '@mui/material';
import React, { Fragment, useMemo } from 'react';
import { Accordion, AccordionItem } from 'components/Accordion';
import {
  acceptedStatuses,
  rejectedStatuses
} from 'modules/conversations/constants';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import { Tooltip } from 'components/Tooltip';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import {
  ConversationStatus,
  ScreeningQuestionOutcome,
  ScreeningQuestionOutcomeType
} from 'generated-graphql/types';

const titleContainer: SxProps = {
  display: 'flex',
  alignItems: 'center',
  gap: 1
};

// The accordion theme sizes summary icons at 16px; the design's knockout check
// is 20px.
const titleCheckIconSx: SxProps = {
  color: 'successV2.main',
  '.MuiAccordionSummary-root &': {
    fontSize: '20px'
  }
};

const passedIconSx: SxProps = {
  color: 'successV2.main'
};

const notCompletedIconSx: SxProps = {
  color: 'warning.light'
};

type Props = {
  status: ConversationStatus;
  screeningQuestionsOutcome: ScreeningQuestionOutcome[];
};

export const ScreeningStage: React.FC<Props> = ({
  status,
  screeningQuestionsOutcome
}) => {
  const { track } = useTracking();

  const isPassed = acceptedStatuses.includes(status);
  const isFailed = rejectedStatuses.includes(status);
  const isInProgress = !isPassed && !isFailed;
  const isCompleted =
    status === ConversationStatus.Completed ||
    status === ConversationStatus.CompletedNotInterested;

  const title = useMemo(() => {
    if (isPassed) {
      return (
        <Box sx={titleContainer}>
          <CheckIcon sx={titleCheckIconSx} />
          <Typography>Knockout questions</Typography>
        </Box>
      );
    }

    if (isFailed) {
      return (
        <Box sx={titleContainer}>
          <CancelIcon fontSize="small" color="error" />
          <Typography>Knockout questions failed</Typography>
        </Box>
      );
    }

    return 'Screening in progress';
  }, [isFailed, isPassed]);

  const items = useMemo(() => {
    const iconByScreeningOutcomeType = {
      [ScreeningQuestionOutcomeType.Passed]: <CheckIcon sx={passedIconSx} />,
      [ScreeningQuestionOutcomeType.Failed]: <CloseIcon color="error" />,
      [ScreeningQuestionOutcomeType.NotCompleted]: (
        <HelpOutlineOutlinedIcon sx={notCompletedIconSx} />
      )
    };

    return screeningQuestionsOutcome.map((outcome) => {
      return {
        text: outcome.responseSummary || outcome.screeningQuestion,
        icon: iconByScreeningOutcomeType[outcome.questionOutcome],
        outcome: outcome.questionOutcome,
        question: outcome.screeningQuestion
      };
    });
  }, [JSON.stringify(screeningQuestionsOutcome)]);

  if (isCompleted) {
    return null;
  }

  return (
    <Accordion
      title={title}
      disabled={isInProgress}
      onClick={() => {
        track(TrackingEventName.CampaignDashboardConversationScreeningSummary);
      }}
    >
      {items.map((item) => {
        return (
          <AccordionItem
            key={item.text.replaceAll(' ', '_')}
            text={item.text}
            icon={
              item.outcome === ScreeningQuestionOutcomeType.NotCompleted ? (
                <Tooltip title={item.question}>{item.icon}</Tooltip>
              ) : (
                <Fragment>{item.icon}</Fragment>
              )
            }
          />
        );
      })}
    </Accordion>
  );
};
